import React, { useState } from 'react';
import { CircularProgress } from '@mui/material';
import { Stack } from 'new/elements/Stack';
import { Box } from 'new/elements/Box';
import Typography from 'new/elements/TypographySupport';
import { ReactComponent as FolderIcon } from 'new/components/icons/fi-sr-folder.svg';
import { ReactComponent as FolderIconSelected } from 'new/components/icons/FolderSelectedIconSolid.svg';
import { ReactComponent as FileIcon } from 'new/components/icons/fileOutlined.svg';
import { ReactComponent as FileIconSelected } from 'new/components/icons/fileSelectedSolid.svg';
import { ReactComponent as EyeIcon } from 'new/components/icons/fi-rr-eye.svg';
import { ReactComponent as CopyIcon } from 'new/components/icons/copy.svg';
import classnames from 'classnames';
import { Virtuoso } from 'react-virtuoso';
import type { TreeNode } from '../BreadcrumbTreeView/types';
import type { FileListProps } from './types';
import { useFileListScroll } from './useFileListScroll';
import { parseTimestampToMs } from './utils/parseTimestampToMs';
import { ExcludeByDateBadge } from './components/ExcludeByDateBadge';
import { fallbackCopyTextToClipboard } from 'js/lib/MiscUtils';
import styles from './FileList.module.scss';
import { FolderOpen as FolderOpenIcon } from '@mui/icons-material';
import { createFileIconResolver, asIconComponent } from 'new/utils/fileIcons';
import { defaultMappingsAdapter } from 'new/utils/fileIcons/adapters/DefaultAdapter';

const fileIconResolver = createFileIconResolver({
  adapter: defaultMappingsAdapter,
  cacheEnabled: true,
  fallbackIcon: asIconComponent(FileIcon),
});

interface RowSelectionState {
  isFolder: boolean;
  isSelected: boolean;
  isFileSelectable: boolean;
}

const resolveRowIcon = (
  node: TreeNode,
  { isFolder, isSelected, isFileSelectable }: RowSelectionState,
) => {
  if (isFolder) return isSelected ? FolderIconSelected : FolderIcon;
  if (isSelected && isFileSelectable) return FileIconSelected;
  return fileIconResolver.resolveIcon(node.displayName || node.name || '', node.mimeType);
};

export const FileList: React.FC<FileListProps> = ({
  data,
  fileIngestModifiedAfterMs,
  selectedNodeId,
  onNodePreview,
  onNodeSelected,
  onNodeExpandClicked,
  selectableFileMimeType,
  enableFileSelection,
  isLoadingData,
  searchValue,
  onClearSearch,
  hideCopyPath = false,
}) => {
  const { virtuosoRef, filteredItems, handleVirtuosoScroll } = useFileListScroll({
    data,
    selectedNodeId,
    searchValue,
    isLoadingData,
  });

  const [copiedNodeId, setCopiedNodeId] = useState<string | null>(null);
  const copiedTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }
    };
  }, []);

  if (isLoadingData) {
    return (
      <Stack alignItems="center" justifyContent="center" padding={4}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  if (!filteredItems.length) {
    return (
      <Stack alignItems="center" justifyContent="center" padding={4} spacing={2}>
        <FolderOpenIcon
          style={{
            fontSize: '48px',
            color: 'var(--color-greyscale-400, #9293a0)',
          }}
        />

        <Typography variant="body2" color="textSecondary" textAlign="center">
          {searchValue
            ? 'No files or folders found matching your search'
            : 'No files or folders found'}
        </Typography>
      </Stack>
    );
  }

  const handleItemClick = (node: TreeNode) => {
    const shouldSelectItem =
      (enableFileSelection &&
        (!selectableFileMimeType || node.mimeType === selectableFileMimeType)) ||
      node.type === 'folder';

    if (!shouldSelectItem) return;

    onNodeSelected(node, node.id);
  };

  const handleItemDoubleClick = (node: TreeNode) => {
    if (node.type === 'folder') {
      if (onClearSearch && searchValue && searchValue.trim()) {
        onClearSearch();
      }
      onNodeExpandClicked(node);
    }
  };

  const handlePreviewClick = (e: React.MouseEvent, node: TreeNode) => {
    e.stopPropagation();
    onNodePreview(node, node.id);
  };

  const handleFolderNameClick = (e: React.MouseEvent, node: TreeNode) => {
    e.stopPropagation();
    if (onClearSearch && searchValue && searchValue.trim()) {
      onClearSearch();
    }
    onNodeExpandClicked(node);
  };

  const handleCopyPathClick = async (e: React.MouseEvent, node: TreeNode) => {
    e.stopPropagation();

    const pathToCopy = node.path || node.id;

    const success = await fallbackCopyTextToClipboard(pathToCopy);
    if (success !== false) {
      // Clear any existing timeout to prevent multiple timers
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }

      setCopiedNodeId(node.id);
      copiedTimeoutRef.current = setTimeout(() => {
        setCopiedNodeId(null);
        copiedTimeoutRef.current = null;
      }, 2000);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const formatDate = (timestamp: number | string): string => {
    if (!timestamp) return '';

    const timestampMs = parseTimestampToMs(timestamp);
    if (timestampMs === null) return '';

    const date = new Date(timestampMs);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const itemContent = (index: number, node: TreeNode) => {
    const isSelected = node.id === selectedNodeId;
    const isFolder = node.type === 'folder';
    const nodeUpdatedAtMs = !isFolder && node.updatedAt ? parseTimestampToMs(node.updatedAt) : null;
    const isExcludedByDateFilter =
      !isFolder &&
      fileIngestModifiedAfterMs != null &&
      nodeUpdatedAtMs != null &&
      nodeUpdatedAtMs <= fileIngestModifiedAfterMs;

    // Determine if a file is selectable (for showing selected icon with checkmark)
    // IMPORTANT: This logic MUST match the logic in handleItemClick above
    // If handleItemClick's selectability logic changes, update this as well
    const isFileSelectable =
      !isFolder &&
      enableFileSelection &&
      (!selectableFileMimeType || node.mimeType === selectableFileMimeType);
    const FileIconComponent = resolveRowIcon(node, { isFolder, isSelected, isFileSelectable });

    const modifiedAfterCutoffMs = fileIngestModifiedAfterMs ?? null;
    const formattedModifiedAfterCutoff =
      modifiedAfterCutoffMs != null ? formatDate(modifiedAfterCutoffMs) : '';
    const excludeByDateTooltip =
      formattedModifiedAfterCutoff !== ''
        ? `Matching ${formattedModifiedAfterCutoff} filter`
        : 'Matching modified-after filter';
    const excludeByDateAriaLabel = `${excludeByDateTooltip}. File excluded from ingest by this filter.`;

    return (
      <Box
        className={classnames(styles.container, {
          [styles.selected]: isSelected,
          [styles.selectableRow]: isFileSelectable,
          [styles.excludedByDateFilter]: isExcludedByDateFilter,
        })}
        onClick={() => handleItemClick(node)}
        onDoubleClick={() => handleItemDoubleClick(node)}
      >
        <Stack direction="row" alignItems="center" sx={{ width: '100%', gap: 'var(--spacing-lg)' }}>
          <FileIconComponent className={styles.icon} />
          {isFolder ? (
            <Typography
              variant="body2"
              noWrap
              className={styles.folderName}
              role="button"
              tabIndex={0}
              onClick={(e) => handleFolderNameClick(e, node)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleFolderNameClick(e as any, node);
                }
              }}
            >
              {node.displayName || node.name}
            </Typography>
          ) : (
            <div className={styles.fileInfo}>
              <Typography variant="body2" noWrap className={styles.fileName}>
                {node.displayName || node.name}
              </Typography>
              {(node.updatedAt || node.size != null) && (
                <div className={styles.fileMetadata}>
                  {node.updatedAt && `Modified: ${formatDate(node.updatedAt)}`}
                  {node.updatedAt && node.size != null && ' • '}
                  {node.size != null && `Size: ${formatFileSize(node.size)}`}
                </div>
              )}
            </div>
          )}
          {!isFolder && (
            <div className={styles.fileActions}>
              {!hideCopyPath && (
                <div
                  role="button"
                  tabIndex={0}
                  className={styles.copyPathButton}
                  onClick={(e) => handleCopyPathClick(e, node)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleCopyPathClick(e as any, node);
                    }
                  }}
                  aria-label={copiedNodeId === node.id ? 'Copied' : 'Copy File Path'}
                >
                  {copiedNodeId === node.id ? (
                    <Typography variant="caption" className={styles.copiedText}>
                      Copied
                    </Typography>
                  ) : (
                    <>
                      <CopyIcon className={styles.actionIcon} />
                      <Typography variant="caption" className={styles.actionLink}>
                        Copy Path
                      </Typography>
                    </>
                  )}
                </div>
              )}
              {isExcludedByDateFilter ? (
                <ExcludeByDateBadge
                  tooltipTitle={excludeByDateTooltip}
                  ariaLabel={excludeByDateAriaLabel}
                />
              ) : null}
              <div
                role="button"
                tabIndex={0}
                className={styles.previewButton}
                onClick={(e) => handlePreviewClick(e, node)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handlePreviewClick(e as any, node);
                  }
                }}
                aria-label="Preview"
              >
                <EyeIcon className={styles.actionIcon} />
                <Typography variant="caption" className={styles.actionLink}>
                  Preview
                </Typography>
              </div>
            </div>
          )}
        </Stack>
      </Box>
    );
  };

  return (
    <div className={styles.fileList}>
      <Virtuoso
        ref={virtuosoRef}
        style={{ height: '100%' }}
        data={filteredItems}
        itemContent={itemContent}
        followOutput={false}
        increaseViewportBy={{ top: 100, bottom: 100 }}
        onScroll={handleVirtuosoScroll}
      />
    </div>
  );
};
